(() => {
  const KEYS = ["work", "conference", "meeting", "focus", "entrance", "reception", "relax", "storage"];
  const CARD_SELECTOR = '.collection-list__item[data-office-legend]';
  const TEXT_LIMIT = 180;
  const cleanText = (value, limit = TEXT_LIMIT) => typeof value === 'string' ? value.trim().slice(0, limit) : '';

  function normalizeSeriesUse(manifest) {
    const descriptions = new Map();
    if (manifest?.version !== 1 || !manifest.byId || typeof manifest.byId !== 'object' || Array.isArray(manifest.byId)) return descriptions;
    const entries = Object.entries(manifest.byId);
    if (entries.length > 1000) return descriptions;
    for (const [id, value] of entries) {
      if (!/^202601:[1-9]\d*$/.test(id) || typeof value !== 'string') continue;
      const description = value.trim();
      if (description && [...description].length <= 14 && !/[\u0000-\u001f\u007f<>\u2028\u2029]/.test(description)) descriptions.set(id, description);
    }
    return descriptions;
  }

  function permittedImage(value) {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && !url.port && !url.username && !url.password && /^\/s\/files\//.test(url.pathname) && !url.hash ? url.href : null;
    } catch (_) { return null; }
  }

  function permittedProduct(value, origin) {
    if (typeof value !== 'string' || /[\u0000-\u0020\\]/.test(value)) return null;
    try {
      const url = new URL(value, origin);
      const variants = url.searchParams.getAll('variant');
      const match = url.pathname.match(/^\/products\/([^/]+)\/?$/);
      const handle = match ? decodeURIComponent(match[1]) : '';
      return url.origin === origin && !url.username && !url.password && !url.hash && /^[\p{L}\p{N}\p{M}][\p{L}\p{N}\p{M}_-]*$/u.test(handle) && variants.length === 1 && /^[1-9]\d*$/.test(variants[0]) && [...url.searchParams.keys()].every((key) => key === 'variant') ? `/products/${encodeURIComponent(handle)}?variant=${variants[0]}` : null;
    } catch (_) { return null; }
  }

  function permittedBrowse(value, currentUrl) {
    try {
      const current = new URL(currentUrl);
      const url = new URL(value, current);
      return typeof value === 'string' && value.startsWith('#') && url.origin === current.origin && url.pathname === current.pathname && url.search === current.search && /^#[a-z0-9_-]+$/i.test(url.hash) ? url.hash : null;
    } catch (_) { return null; }
  }

  function permittedCategory(value, origin, nodeId) {
    if (typeof value !== 'string' || /[\u0000-\u0020\\]/.test(value)) return null;
    try {
      const url = new URL(value, origin);
      const match = url.pathname.match(/^\/collections\/odp-202601-([1-9]\d*)$/);
      return url.origin === origin && !url.username && !url.password && !url.search && !url.hash && match && (!nodeId || nodeId === `202601:${match[1]}`) ? url.pathname : null;
    } catch (_) { return null; }
  }

  function resolveDestination(item, origin, resolver) {
    if (typeof resolver?.resolve === 'function') {
      try {
        const result = resolver.resolve(item, { origin });
        const href = result?.kind === 'product' ? permittedProduct(result.href, origin) : result?.kind === 'category' ? permittedCategory(result.href, origin) : null;
        return href ? { href, kind: result.kind } : { href: null, kind: 'unavailable' };
      } catch (_) {}
    } else if (item.canLink === true && (item.status === undefined || item.status === 'ACTIVE')) {
      const variant = String(item.variantId ?? '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      const exactVariant = /^[1-9]\d*$/.test(variant) && (typeof item.variantId !== 'number' || Number.isSafeInteger(item.variantId));
      const handle = typeof item.handle === 'string' && /^[\p{L}\p{N}\p{M}][\p{L}\p{N}\p{M}_-]*$/u.test(item.handle) ? item.handle : '';
      const value = item.handle ? (handle && exactVariant ? `/products/${encodeURIComponent(handle)}?variant=${variant}` : null) : item.productUrl;
      let href = permittedProduct(value, origin);
      if (href && item.variantId != null && (!exactVariant || new URL(href, origin).searchParams.get('variant') !== variant)) href = null;
      if (href) return { href, kind: 'product' };
    }
    // Static routes come only from the verified manifest and must name the same exact ODP node.
    const href = /^202601:[1-9]\d*$/.test(item.categoryNodeId || '') ? permittedCategory(item.categoryUrl, origin, item.categoryNodeId) : null;
    return href ? { href, kind: 'category' } : { href: null, kind: 'unavailable' };
  }

  function normalizeGroup(group, origin, resolver) {
    const hashes = new Set();
    const items = [];
    for (const raw of Array.isArray(group?.items) ? group.items : []) {
      if (!raw || typeof raw !== 'object') continue;
      const imageUrl = permittedImage(raw.imageUrl);
      const imageHash = cleanText(raw.imageHash, 128).toLowerCase();
      const id = cleanText(String(raw.id ?? raw.productId ?? raw.sku ?? ''), 100);
      const sku = cleanText(raw.sku, 100);
      const title = cleanText(raw.title) || sku;
      if (!imageUrl || !/^[a-f0-9]{32,128}$/.test(imageHash) || !title || !id || !sku || hashes.has(imageHash)) continue;
      hashes.add(imageHash);
      const destinationItem = { sku, productId: raw.productId, variantId: raw.variantId, handle: raw.handle, status: raw.status, canLink: raw.canLink === true, productUrl: raw.productUrl, categoryNodeId: raw.categoryNodeId, categoryUrl: raw.categoryUrl };
      const destination = resolveDestination(destinationItem, origin, resolver);
      items.push({ id, sku, title, imageUrl, imageHash, inquiryOnly: raw.inquiryOnly === true, destinationItem, destination, productUrl: destination.kind === 'product' ? destination.href : null, canLink: destination.kind === 'product' });
      if (items.length === 30) break;
    }
    return { items, imageCount: items.length, readyCount: items.filter((item) => item.canLink).length };
  }

  function normalizeSelection(selection, origin, resolver) {
    const group = normalizeGroup(selection, origin, resolver);
    if (!group.imageCount) return null;
    if (!selection?.centerSku) return { ...group, centerItem: null };
    const centerItem = group.items.find(item => item.sku === selection.centerSku);
    if (!centerItem) return null;
    const items = group.items.filter(item => item.sku !== centerItem.sku && item.imageHash !== centerItem.imageHash);
    if (items.length < 5) return null;
    return { items, centerItem, imageCount: items.length, readyCount: items.filter(item => item.canLink).length };
  }

  function normalizeRelatedManifest(manifest, origin, resolver) {
    const items = new Map(), relatedBySku = new Map();
    if (manifest?.version !== 1 || !manifest.items || Array.isArray(manifest.items) || !manifest.relatedBySku || Array.isArray(manifest.relatedBySku)) return { items, relatedBySku };
    for (const [sku, raw] of Object.entries(manifest.items)) {
      if (raw?.sku !== sku) continue;
      const item = normalizeGroup({ items: [raw] }, origin, resolver).items[0];
      if (!item) continue;
      item.destinationItem.classification = true;
      item.destination = classificationDestination(item.destinationItem, origin, resolver);
      item.canLink = item.destination.kind === 'product';
      item.productUrl = item.canLink ? item.destination.href : null;
      items.set(sku, item);
    }
    for (const [sku, values] of Object.entries(manifest.relatedBySku)) if (Array.isArray(values)) relatedBySku.set(sku, values.filter(value => typeof value === 'string' && items.has(value)));
    return { items, relatedBySku };
  }

  function relatedItems(source, catalog) {
    if (!source || !catalog?.items || !catalog.relatedBySku) return [];
    const canonical = catalog.items.get(source.sku);
    const sourceVariants = new Set([source.destinationItem?.variantId, canonical?.destinationItem?.variantId].filter(Boolean).map(value => String(value).replace(/^gid:\/\/shopify\/ProductVariant\//, '')));
    const hashes = new Set([source.imageHash, canonical?.imageHash].filter(Boolean)), skus = new Set([source.sku]), items = [];
    for (const sku of catalog.relatedBySku.get(source.sku) || []) {
      const item = catalog.items.get(sku), variant = String(item?.destinationItem?.variantId || '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      if (!item || skus.has(sku) || hashes.has(item.imageHash) || (variant && sourceVariants.has(variant))) continue;
      skus.add(sku); hashes.add(item.imageHash); items.push(item);
      if (items.length === 30) break;
    }
    return items;
  }

  function classificationDestination(item, origin, resolver) {
    // Classification previews have an exact source category even before the
    // public catalog is ready. Only the shared resolver may prove a product.
    if (typeof resolver?.resolve === 'function') {
      try {
        const result = resolver.resolve(item, { origin });
        const href = result?.kind === 'product' ? permittedProduct(result.href, origin) : null;
        if (href) return { href, kind: 'product' };
      } catch (_) {}
    }
    const href = permittedCategory(item.categoryUrl, origin, item.categoryNodeId);
    return { href, kind: href ? 'category' : 'unavailable' };
  }

  function normalizeClassification(node, origin, resolver) {
    const combined = node?.title === 'その他' && Array.isArray(node.sourceNodeIds) && node.sourceNodeIds.length === 2 && new Set(node.sourceNodeIds).size === 2 && node.sourceNodeIds.every(id => ['202601:4930', '202601:4933'].includes(id));
    const sources = combined ? node.browseLinks : [{ nodeId: node?.nodeId, title: node?.title, categoryUrl: node?.categoryUrl }];
    if (!Array.isArray(sources) || sources.length !== (combined ? 2 : 1)) return null;
    const browseLinks = sources.map(source => ({ nodeId: source.nodeId, title: cleanText(source.title), categoryUrl: /^202601:[1-9]\d*$/.test(source.nodeId || '') ? permittedCategory(source.categoryUrl, origin, source.nodeId) : null }));
    if (!cleanText(node?.title) || browseLinks.some(source => !source.categoryUrl || !source.title) || (combined && !['202601:4930', '202601:4933'].every(id => browseLinks.some(source => source.nodeId === id)))) return null;
    const allowed = new Set(browseLinks.map(source => source.nodeId));
    const group = normalizeGroup({ items: (node.items || []).filter(item => allowed.has(item.categoryNodeId) && item.nodeIds?.includes(item.categoryNodeId)) }, origin, resolver);
    group.items.forEach(item => {
      item.destinationItem.classification = true;
      item.destination = classificationDestination(item.destinationItem, origin, resolver);
      item.canLink = item.destination.kind === 'product';
      item.productUrl = item.canLink ? item.destination.href : null;
    });
    return { ...group, readyCount: group.items.filter(item => item.canLink).length, title: cleanText(node.title), browseLinks, browseUrl: browseLinks[0].categoryUrl, classification: true };
  }

  function ellipsePoints(count, rx, ry, phase = 0) {
    const samples = 1440;
    const lengths = [0];
    let total = 0;
    for (let i = 1; i <= samples; i += 1) {
      const angle = (i - .5) * Math.PI * 2 / samples;
      total += Math.hypot(rx * Math.sin(angle), ry * Math.cos(angle)) * Math.PI * 2 / samples;
      lengths.push(total);
    }
    return Array.from({ length: count }, (_, index) => {
      const target = ((index + phase) / count % 1) * total;
      let low = 0;
      let high = samples;
      while (low + 1 < high) {
        const middle = (low + high) >> 1;
        if (lengths[middle] < target) low = middle;
        else high = middle;
      }
      const fraction = (target - lengths[low]) / (lengths[high] - lengths[low] || 1);
      const angle = (low + fraction) * Math.PI * 2 / samples;
      return { x: rx * Math.cos(angle), y: ry * Math.sin(angle), rotation: Math.sin(angle * 2) * 5 };
    });
  }

  function ringGeometry(count, width, height, center = { width: 230, height: 144 }, options = {}) {
    if (!count || count > 30 || width < 1000 || height < 500) return null;
    const ratio = 1.15;
    const patterns = [];
    const rx = width / 2 - 85;
    if (count <= 12) patterns.push(ellipsePoints(count, rx * .82, (height / 2 - 80) * .82, .5));
    else {
      for (const innerCount of new Set([Math.round(count / 3), Math.round(count * .4)])) {
        for (const margin of [66, 78]) {
          for (const factor of [.55, .6]) {
            for (const phase of [0, .25]) {
              const ry = height / 2 - margin;
              patterns.push([...ellipsePoints(innerCount, rx * factor, ry * factor, phase), ...ellipsePoints(count - innerCount, rx, ry, .5)]);
            }
          }
        }
      }
    }
    // Classification centers also contain source/series actions. Give those
    // taller centers side petals without changing the eight space layouts.
    if (center.height > 180 || options.sidePetals === true) {
      if (count <= 20) patterns.push(ellipsePoints(count, rx, height / 2 - (options.compact ? 48 : 66), .5));
      for (const innerCount of [4, 6, 8]) {
        if (count <= innerCount) continue;
        for (const sideX of [.38, .44, .5]) for (const sideY of [.32, .4, .48]) {
          const inner = Array.from({ length: innerCount }, (_, index) => ({ x: (index % 2 ? -1 : 1) * rx * sideX, y: (Math.floor(index / 2) / (innerCount / 2 - 1) - .5) * height * sideY, rotation: 0 }));
          patterns.push([...inner, ...ellipsePoints(count - innerCount, rx, height / 2 - (options.compact ? 48 : 66), .5)]);
        }
      }
    }
    let bestSize = 0;
    let bestPoints;
    for (const points of patterns) {
      const fits = (size) => {
        const boxes = points.map((point) => {
          const angle = Math.abs(point.rotation) * Math.PI / 180;
          return { x: point.x, y: point.y, width: size * Math.cos(angle) + size / ratio * Math.sin(angle) + 14, height: size / ratio * Math.cos(angle) + size * Math.sin(angle) + 14 };
        });
        for (let i = 0; i < boxes.length; i += 1) {
          const box = boxes[i];
          if (Math.abs(box.x) + box.width / 2 > width / 2 - 9 || Math.abs(box.y) + box.height / 2 > height / 2 - 9) return false;
          if (Math.abs(box.x) < (box.width + center.width) / 2 + 10 && Math.abs(box.y) < (box.height + center.height) / 2 + 10) return false;
          for (let j = 0; j < i; j += 1) {
            const other = boxes[j];
            if (Math.abs(box.x - other.x) < (box.width + other.width) / 2 && Math.abs(box.y - other.y) < (box.height + other.height) / 2) return false;
          }
        }
        return true;
      };
      let low = 0;
      let high = count <= 12 ? 216 : 200;
      for (let i = 0; i < 16; i += 1) {
        const middle = (low + high) / 2;
        if (fits(middle)) low = middle;
        else high = middle;
      }
      if (low > bestSize) { bestSize = low; bestPoints = points; }
    }
    if (bestSize < (options.compact === true ? 54 : 76)) return null;
    const size = Math.floor(bestSize);
    return bestPoints.map((point) => ({ ...point, dx: point.x, dy: point.y, x: point.x + width / 2, y: point.y + height / 2, width: size, height: size / ratio }));
  }

  function outerEnvelope(rectangles, padding = 10) {
    const points = rectangles.filter((r) => r && [r.left, r.top, r.right, r.bottom].every(Number.isFinite) && r.right > r.left && r.bottom > r.top).flatMap((r) => [
      { x: r.left - padding, y: r.top - padding }, { x: r.right + padding, y: r.top - padding },
      { x: r.right + padding, y: r.bottom + padding }, { x: r.left - padding, y: r.bottom + padding }
    ]).sort((a, b) => a.x - b.x || a.y - b.y);
    const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    const half = (values) => {
      const result = [];
      for (const point of values) {
        while (result.length > 1 && cross(result.at(-2), result.at(-1), point) <= 0) result.pop();
        result.push(point);
      }
      result.pop();
      return result;
    };
    return points.length ? [...half(points), ...half([...points].reverse())] : [];
  }

  function insideEnvelope(point, hull) {
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y) || hull.length < 3) return false;
    let sign = 0;
    for (let i = 0; i < hull.length; i += 1) {
      const a = hull[i], b = hull[(i + 1) % hull.length];
      const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
      if (Math.abs(cross) < .01) continue;
      if (sign && Math.sign(cross) !== sign) return false;
      sign = Math.sign(cross);
    }
    return true;
  }

  const api = { normalizeSeriesUse, normalizeSelection, normalizeRelatedManifest, relatedItems, outerEnvelope, insideEnvelope, normalizeGroup, normalizeClassification, classificationDestination, resolveDestination, permittedCategory, permittedImage, permittedProduct, permittedBrowse, ellipsePoints, ringGeometry };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('ace-category-flower')) return;

  class AceCategoryFlower extends HTMLElement {
    async connectedCallback() {
      this.ready = false;
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.scope = this.closest('[data-office-explorer]');
      if (!this.scope || typeof this.dialog?.showModal !== 'function') return;
      this.stage = this.querySelector('[data-flower-stage]');
      this.center = this.querySelector('[data-flower-center]');
      this.petals = this.querySelector('[data-flower-petals]');
      this.closeButton = this.querySelector('[data-flower-close]');
      this.browse = this.querySelector('[data-flower-browse]');
      this.sourceLinks = this.querySelector('[data-flower-sources]');
      this.seriesButton = this.querySelector('[data-flower-series]');
      this.backButton = this.querySelector('[data-flower-back]');
      this.seriesPages = this.querySelector('[data-flower-pages]');
      this.previousPage = this.querySelector('[data-flower-page-prev]');
      this.nextPage = this.querySelector('[data-flower-page-next]');
      this.sourcePetals = this.querySelector('[data-flower-source]');
      this.sourceTitle = this.querySelector('[data-flower-source-title]');
      this.selectionDescription = this.querySelector('[data-flower-selection-description]');
      this.browseLabel = this.browse.textContent;
      this.coarseMedia = window.matchMedia('(pointer: coarse)');
      this.cards = new Map();
      this.groups = new Map();
      this.favoriteStates = new Map();
      this.favoriteOperations = new Set();
      this.favoriteRecords = [];
      this.relatedCatalog = { items: new Map(), relatedBySku: new Map() };
      this.relatedPromise = null;
      this.seriesUse = new Map();
      this.seriesUsePromise = null;
      this.layer = 1;
      this.viewTrail = [];
      this.viewMode = 'products';
      try {
        this.categories = JSON.parse(this.querySelector('[data-flower-categories]').textContent);
        const manifestUrl = new URL(this.dataset.manifestUrl, location.href);
        if (manifestUrl.protocol !== 'https:' || (manifestUrl.origin !== location.origin && manifestUrl.hostname !== 'cdn.shopify.com')) return;
        const response = await fetch(manifestUrl.href, { signal, credentials: 'same-origin' });
        if (!response.ok) return;
        const manifest = await response.json();
        if (signal.aborted || !this.isConnected) return;
        for (const key of KEYS) {
          const group = normalizeGroup(manifest.groups?.[key], location.origin, window.AceProductDestination);
          const category = this.categories[key];
          if (!group.imageCount || !cleanText(category?.title)) continue;
          this.groups.set(key, { ...group, title: cleanText(category.title) });
        }
      } catch (_) { return; }
      const options = { signal };
      const destinationsReady = () => {
        this.refreshDestinations();
        const resolver = window.AceProductDestination;
        if (resolver) {
          Promise.resolve(resolver.ready).then(() => { if (!signal.aborted && this.isConnected) this.refreshDestinations(); }, () => {});
        }
      };
      document.addEventListener('ace:product-destination-ready', destinationsReady, options);
      document.addEventListener('ace:board-ready', destinationsReady, options);
      document.addEventListener('ace:catalog-items', destinationsReady, options);
      const favoritesReady = () => {
        for (const [sku, state] of this.favoriteStates) if (state.status === 'unavailable') this.favoriteStates.delete(sku);
        this.refreshFavorites();
        this.favoriteRecords.forEach(({ li, item }) => { if (li.flowerHovered || li.contains(document.activeElement)) this.ensureFavorite(item); });
      };
      document.addEventListener('ace:board-ready', favoritesReady, options);
      document.addEventListener('ace:catalog-items', favoritesReady, options);
      document.addEventListener('ace:board-change', () => this.refreshFavorites(), options);
      destinationsReady();
      this.scope.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        const key = card.dataset.officeLegend;
        if (!this.groups.has(key)) return;
        const links = Array.from(card.querySelectorAll('a')).filter((link) => !link.hasAttribute('data-flower-ignore'));
        const trigger = links.find((link) => link.getAttribute('tabindex') !== '-1') || links[0];
        const browseUrl = permittedBrowse(trigger?.getAttribute('href'), location.href);
        if (!trigger || !browseUrl) return;
        this.groups.get(key).browseUrl = browseUrl;
        this.cards.set(key, { card, trigger, links });
        links.forEach((link) => {
          link.setAttribute('aria-haspopup', 'dialog');
          link.setAttribute('aria-controls', this.id + '-dialog');
          link.setAttribute('aria-expanded', 'false');
        });
        const hover = (event) => {
          if (event.pointerType === 'touch' || event.buttons > 0 || Date.now() < (this.touchCooldown || 0)) return;
          this.scheduleHover(key, trigger);
        };
        card.addEventListener('pointerenter', hover, options);
        card.addEventListener('mouseenter', hover, options);
        card.addEventListener('pointerleave', () => this.cancelHover(), options);
        card.addEventListener('mouseleave', () => this.cancelHover(), options);
        card.addEventListener('pointerdown', (event) => {
          this.cancelHover();
          if (event.pointerType === 'touch') this.touchCooldown = Date.now() + 800;
        }, options);
      });
      this.dialog.id = this.id + '-dialog';
      this.scope.addEventListener('click', (event) => this.activateCard(event), { signal, capture: true });
      this.scope.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') this.activateCard(event);
      }, { signal, capture: true });
      this.closeButton.addEventListener('click', () => this.requestClose(), options);
      this.backButton?.addEventListener('click', () => this.navigateBack(), options);
      this.previousPage?.addEventListener('click', () => this.changeSeriesPage(-1), options);
      this.nextPage?.addEventListener('click', () => this.changeSeriesPage(1), options);
      this.seriesButton?.addEventListener('click', () => {
        const onSeries = this.cards.get(this.activeKey)?.onSeries;
        if (typeof onSeries !== 'function') return;
        if (this.cards.get(this.activeKey)?.seriesInPlace) { onSeries(); return; }
        this.afterClose = onSeries;
        this.requestClose();
      }, options);
      this.browse.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (this.groups.get(this.activeKey)?.classification) return;
        const trigger = this.cards.get(this.activeKey)?.trigger;
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        this.afterClose = () => { this.bypassTrigger = trigger; trigger.click(); this.bypassTrigger = null; };
        this.requestClose();
      }, options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.requestClose(); }, options);
      this.dialog.addEventListener('close', () => {
        if (!this.internalClose && this.activeKey) this.requestClose();
      }, options);
      this.dialog.addEventListener('pointermove', (event) => this.trackBoundary(event), options);
      this.dialog.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch' && this.boundaryArmed) this.scheduleBoundaryClose(true);
      }, options);
      this.dialog.addEventListener('keydown', () => { this.cancelRelatedHover(); this.cancelBoundaryClose(); this.boundaryArmed = false; }, options);
      this.querySelector('[data-flower-viewport]').addEventListener('scroll', () => { this.cancelRelatedHover(); this.cancelBoundaryClose(); }, options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.cancelRelatedHover();
        this.cancelBoundaryClose();
        this.emptyPointerDown = this.isEmptyBackground(event.target);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.emptyPointerDown && this.isEmptyBackground(event.target) && !event.defaultPrevented) this.requestClose();
        this.emptyPointerDown = false;
        const link = event.target.closest('a[href]');
        if (link && this.dialog.contains(link) && !event.defaultPrevented && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) {
          this.prepareNavigation();
        }
      }, options);
      // Opening is a visual overlay only. Native navigation keeps its own history.
      window.addEventListener('popstate', () => this.closeUI(false, false), options);
      window.addEventListener('pageshow', () => this.closeUI(false, false), options);
      window.addEventListener('pagehide', () => this.closeUI(false, false), options);
      window.addEventListener('blur', () => { this.cancelHover(); this.cancelRelatedHover(); }, options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) { this.cancelHover(); this.cancelRelatedHover(); } }, options);
      this.resizeObserver = new ResizeObserver(() => this.queueLayout());
      this.resizeObserver.observe(this.dialog);
      this.coarseMedia.addEventListener('change', () => this.queueLayout(), options);
      this.ready = true;
      this.preloadSeriesUse();
      this.dispatchEvent(new CustomEvent('ace:flower-ready', { bubbles: true }));
      // Also handle a pointer already resting on a card when its data finishes loading.
      this.cards.forEach(({ card, trigger }, key) => {
        if (card.matches(':hover')) this.scheduleHover(key, trigger);
      });
    }

    activateCard(event) {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.type === 'click' && event.button !== 0) || (event.type === 'keydown' && event.repeat)) return;
      const link = event.target.closest('a');
      if (link === this.bypassTrigger) return;
      const card = link?.closest(CARD_SELECTOR);
      const key = card?.dataset.officeLegend;
      const entry = this.cards.get(key);
      if (!entry || !entry.links.includes(link) || entry.card !== card || !this.scope.contains(card) || this.dialog.open) return;
      this.cancelHover();
      if (this.open(key, link)) event.preventDefault();
    }

    cancelHover() { clearTimeout(this.hoverTimer); }

    scheduleHover(key, trigger) {
      this.cancelHover();
      if (this.dialog.open || Date.now() < (this.hoverCooldown || 0)) return;
      this.hoverTimer = setTimeout(() => this.open(key, trigger), 100);
    }

    openClassification(node, trigger, options = {}) {
      if (!this.ready || !trigger?.isConnected || this.dialog.open) return false;
      const group = normalizeClassification(node, location.origin, window.AceProductDestination);
      if (!group) return false;
      const key = `classification:${node.nodeId || '4930+4933'}`;
      this.groups.set(key, group);
      this.cards.set(key, { trigger, links: options.expandedTriggers || [], onSeries: options.onSeries, seriesInPlace: options.seriesInPlace === true });
      return this.open(key, trigger);
    }

    openSelection(selection, trigger) {
      if (!this.ready || !trigger?.isConnected || this.dialog.open || !/^[a-z][a-z0-9_-]{0,63}$/.test(selection?.key || '') || !cleanText(selection?.title)) return false;
      const group = normalizeSelection(selection, location.origin, window.AceProductDestination);
      if (!group) return false;
      const key = `selection:${selection.key}`;
      this.groups.set(key, { ...group, title: cleanText(selection.title), description: cleanText(selection.description, 300), selection: true });
      this.cards.set(key, { trigger, links: [trigger] });
      return this.open(key, trigger);
    }

    open(key, trigger) {
      const group = this.groups.get(key);
      if ((!group?.browseUrl && !group?.selection) || !this.cards.has(key) || this.dialog.open || document.querySelector('dialog[open]')) return false;
      this.activeKey = key;
      this.viewTrail = [];
      this.viewMode = 'products';
      this.seriesView = null;
      this.dialog.dataset.flowerKind = group.selection ? 'selection' : group.classification ? 'classification' : 'space';
      this.boundaryArmed = false;
      this.lastBoundaryPoint = null;
      this.cancelBoundaryClose();
      this.refreshDestinations();
      this.trigger = trigger || this.cards.get(key)?.trigger;
      this.layer = 1;
      this.relatedSource = null;
      this.relatedReturn = null;
      this.relatedHoverSuppressed = false;
      this.renderCenter(group);
      this.favoriteFeedback('');
      this.renderItems(group.items, group.centerItem);
      this.preloadRelated();
      this.lockScroll();
      try { this.dialog.showModal(); } catch (_) { this.unlockScroll(); this.activeKey = null; return false; }
      window.scrollTo({ left: this.scrollLock.x, top: this.scrollLock.y, behavior: 'instant' });
      this.cards.get(key)?.links.forEach((link) => link.setAttribute('aria-expanded', 'true'));
      this.querySelector('[data-flower-viewport]').scrollTop = 0;
      this.querySelector('[data-flower-title]').focus({ preventScroll: true });
      this.queueLayout();
      return true;
    }

    captureView() {
      const focused = this.favoriteRecords.find(record => record.li.contains(document.activeElement));
      return {
        kind: this.viewMode, key: this.activeKey, group: this.groups.get(this.activeKey), card: this.cards.get(this.activeKey),
        series: this.seriesView, scrollTop: this.querySelector('[data-flower-viewport]').scrollTop,
        focusSku: focused?.item.sku, focusRole: focused?.button === document.activeElement ? 'star' : focused?.relatedButton === document.activeElement ? 'related' : 'photo',
        focusSeries: document.activeElement === this.seriesButton, focusChoice: document.activeElement?.dataset?.flowerChoice
      };
    }

    openSeriesSelection(selection, trigger) {
      if (!this.ready || !trigger?.isConnected || this.layer === 2 || this.viewMode === 'series' || !/^[a-z][a-z0-9_-]{0,63}$/.test(selection?.key || '') || !cleanText(selection?.title) || !Array.isArray(selection.choices)) return false;
      const seen = new Set(), images = new Set(), choices = [];
      for (const node of selection.choices) {
        if (!/^202601:[1-9]\d*$/.test(node?.nodeId || '') || seen.has(node.nodeId)) continue;
        const group = normalizeClassification(node, location.origin, window.AceProductDestination);
        if (!group) continue;
        seen.add(node.nodeId);
        const image = group.items.find(item => !images.has(item.imageHash)) || group.items[0] || null;
        if (image) images.add(image.imageHash);
        choices.push({ id: node.nodeId, group, title: group.title, image });
      }
      if (!choices.length) return false;
      const series = { key: selection.key, title: cleanText(selection.title), choices, page: 0 };
      if (this.dialog.open) { this.viewTrail.push(this.captureView()); this.beginLayerTransition(); }
      else {
        const key = 'series:active', group = { title: series.title, items: [], imageCount: 0, readyCount: 0, selection: true };
        this.groups.set(key, group); this.cards.set(key, { trigger, links: [trigger] });
        if (!this.open(key, trigger)) return false;
      }
      this.viewMode = 'series'; this.seriesView = series; this.renderSeriesView();
      this.querySelector('[data-flower-viewport]').scrollTop = 0;
      this.querySelector('[data-flower-title]').focus({ preventScroll: true }); this.queueLayout(); return true;
    }

    renderSeriesView() {
      const view = this.seriesView;
      this.layer = 1; this.relatedSource = null;
      this.renderCenter({ title: view.title, selection: true, imageCount: view.choices.length });
      this.dialog.dataset.flowerKind = 'series';
      if (this.seriesButton) this.seriesButton.hidden = true;
      this.browse.hidden = true; if (this.sourceLinks) this.sourceLinks.hidden = true;
      if (this.seriesPages) this.seriesPages.hidden = view.choices.length <= 30;
      if (this.previousPage) this.previousPage.hidden = view.page === 0;
      if (this.nextPage) this.nextPage.hidden = (view.page + 1) * 30 >= view.choices.length;
      this.favoriteFeedback('');
      this.renderController?.abort(); this.renderController = new AbortController();
      this.renderToken = (this.renderToken || 0) + 1; this.favoriteRecords = [];
      const fragment = document.createDocumentFragment(), options = { signal: this.renderController.signal };
      for (const [index, choice] of view.choices.slice(view.page * 30, (view.page + 1) * 30).entries()) {
        const li = document.createElement('li'); li.className = 'ace-category-flower__petal';
        li.style.setProperty('--flower-delay', String(Math.min(index * 8, 160)) + 'ms');
        const button = document.createElement('button'); button.type = 'button'; button.className = 'ace-category-flower__product ace-category-flower__series-choice';
        button.dataset.flowerChoice = choice.id; button.title = choice.title; button.setAttribute('aria-label', choice.title + 'の商品を選ぶ');
        const label = document.createElement('span'); label.className = 'ace-category-flower__caption'; label.hidden = true; label.setAttribute('aria-hidden', 'true');
        const nameText = document.createElement('span'); nameText.textContent = choice.title;
        const use = document.createElement('span'); use.className = 'ace-category-flower__series-use'; use.hidden = true;
        li.flowerSeriesChoice = choice; li.flowerUseLine = use;
        if (choice.image) {
          label.append(nameText, use);
          const img = document.createElement('img'); img.className = 'ace-category-flower__image'; img.src = choice.image.imageUrl; img.alt = ''; img.width = 400; img.height = 360; img.loading = index < 8 ? 'eager' : 'lazy'; img.decoding = 'async'; button.append(img);
        } else {
          const name = document.createElement('span'); name.className = 'ace-category-flower__series-name'; name.append(nameText, use); button.append(name); button.dataset.flowerTextOnly = 'true';
        }
        const update = () => { label.hidden = !choice.image || !(li.flowerHovered || li.contains(document.activeElement)); li.dataset.labelVisible = label.hidden ? 'false' : 'true'; if (!label.hidden) this.positionLabel(label); };
        li.updateFlowerReveal = update;
        li.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { li.flowerHovered = true; update(); } }, options);
        li.addEventListener('pointerleave', () => { li.flowerHovered = false; update(); }, options);
        button.addEventListener('focus', update, options);
        button.addEventListener('blur', () => { li.flowerHovered = false; label.hidden = true; li.dataset.labelVisible = 'false'; }, options);
        button.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); this.openSeriesChoice(choice); }, options);
        li.append(button, label); fragment.append(li);
      }
      this.petals.replaceChildren(fragment);
      this.updateSeriesUse();
    }

    preloadSeriesUse() {
      if (this.seriesUsePromise) return this.seriesUsePromise;
      if (!this.dataset.seriesUseUrl) return Promise.resolve();
      const signal = this.controller.signal, request = new AbortController();
      const abort = () => request.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 5000);
      this.seriesUsePromise = (async () => {
        try {
          const url = new URL(this.dataset.seriesUseUrl, location.href);
          if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com') || url.username || url.password || url.port) return;
          const response = await fetch(url.href, { signal: request.signal, credentials: 'same-origin' });
          if (!response.ok || Number(response.headers?.get('content-length') || 0) > 65536) return;
          const text = await response.text();
          if (text.length > 65536 || signal.aborted || request.signal.aborted || !this.isConnected) return;
          this.seriesUse = normalizeSeriesUse(JSON.parse(text));
          this.updateSeriesUse();
        } catch (_) {} finally {
          clearTimeout(timeout);
          signal.removeEventListener('abort', abort);
        }
      })();
      return this.seriesUsePromise;
    }

    updateSeriesUse() {
      if (this.viewMode !== 'series') return;
      for (const li of this.petals.children) {
        const choice = li.flowerSeriesChoice, use = li.flowerUseLine;
        if (!choice || !use) continue;
        const description = this.seriesUse.get(choice.id) || '';
        use.textContent = description; use.hidden = !description;
        li.children[0].setAttribute('aria-label', choice.title + 'の商品を選ぶ' + (description ? '。' + description : ''));
        li.updateFlowerReveal?.();
      }
      this.queueLayout();
    }

    changeSeriesPage(direction) {
      if (!this.dialog.open || this.viewMode !== 'series') return false;
      const page = this.seriesView.page + direction;
      if (page < 0 || page * 30 >= this.seriesView.choices.length) return false;
      this.beginLayerTransition(); this.seriesView.page = page; this.renderSeriesView();
      this.querySelector('[data-flower-viewport]').scrollTop = 0;
      const control = direction > 0 ? this.nextPage : this.previousPage;
      (control && !control.hidden ? control : this.querySelector('[data-flower-title]')).focus({ preventScroll: true }); this.queueLayout(); return true;
    }

    openSeriesChoice(choice) {
      if (!this.dialog.open || this.viewMode !== 'series' || !this.seriesView.choices.includes(choice)) return false;
      const parent = this.captureView(); parent.focusChoice = choice.id; this.viewTrail.push(parent); this.beginLayerTransition();
      this.activeKey = 'series-product:active'; this.groups.set(this.activeKey, choice.group); this.cards.set(this.activeKey, { trigger: this.trigger, links: [] });
      this.viewMode = 'products'; this.layer = 1; this.relatedHoverSuppressed = true; this.relatedUnlockPoint = this.lastBoundaryPoint;
      this.dialog.dataset.flowerKind = 'classification'; this.renderCenter(choice.group); this.favoriteFeedback(''); this.renderItems(choice.group.items);
      this.querySelector('[data-flower-viewport]').scrollTop = 0; this.querySelector('[data-flower-title]').focus({ preventScroll: true }); this.queueLayout(); return true;
    }

    navigateBack() {
      if (this.layer === 2) return this.backRelated();
      if (!this.dialog.open || !this.viewTrail?.length) return false;
      const view = this.viewTrail.pop(); this.beginLayerTransition(); this.activeKey = view.key;
      if (view.group) this.groups.set(view.key, view.group); if (view.card) this.cards.set(view.key, view.card);
      this.viewMode = view.kind; this.seriesView = view.series; this.layer = 1; this.relatedHoverSuppressed = true; this.relatedUnlockPoint = this.lastBoundaryPoint;
      let target;
      if (view.kind === 'series') {
        this.renderSeriesView();
        target = Array.from(this.petals.children).map(li => li.children[0]).find(button => button.dataset.flowerChoice === view.focusChoice);
      } else {
        this.dialog.dataset.flowerKind = view.group.selection ? 'selection' : view.group.classification ? 'classification' : 'space';
        this.renderCenter(view.group); this.favoriteFeedback(''); this.renderItems(view.group.items, view.group.centerItem);
        const record = this.favoriteRecords.find(record => record.item.sku === view.focusSku);
        target = view.focusRole === 'star' ? record?.button : view.focusRole === 'related' ? record?.relatedButton : record?.tile;
        if (!target && view.focusSeries && !this.seriesButton?.hidden) target = this.seriesButton;
      }
      this.querySelector('[data-flower-viewport]').scrollTop = view.scrollTop || 0;
      (target || this.querySelector('[data-flower-title]')).focus({ preventScroll: true }); this.queueLayout(); return true;
    }

    renderCenter(group) {
      const centeredSelection = this.layer === 1 && !!group.centerItem;
      this.dialog.dataset.flowerCenterProduct = centeredSelection ? 'true' : 'false';
      if (this.selectionDescription) { this.selectionDescription.hidden = !centeredSelection; this.selectionDescription.textContent = centeredSelection ? group.description || group.title : ''; }
      this.dialog.dataset.flowerLayer = String(this.layer);
      this.dialog.dataset.flowerView = this.viewMode;
      this.closeButton.setAttribute('aria-label', this.layer === 2 ? '元の商品一覧に戻る' : this.viewTrail?.length ? '前の花弁に戻る' : '花弁を閉じる');
      if (this.backButton) this.backButton.hidden = this.layer !== 2 && !this.viewTrail?.length;
      if (this.seriesPages) this.seriesPages.hidden = true;
      if (this.sourcePetals) { this.sourcePetals.hidden = this.layer !== 2 && !centeredSelection; this.sourcePetals.replaceChildren(); }
      if (this.sourceTitle) { this.sourceTitle.hidden = this.layer !== 2; this.sourceTitle.textContent = this.relatedSource?.title || ''; this.sourceTitle.title = this.relatedSource?.title || ''; }
      if (this.layer === 2) {
        this.querySelector('[data-flower-title]').textContent = '関連商品';
        this.browse.hidden = true;
        if (this.seriesButton) this.seriesButton.hidden = true;
        if (this.sourceLinks) this.sourceLinks.hidden = true;
        const empty = this.querySelector('[data-flower-empty]'); if (empty) empty.hidden = true;
        return;
      }
      if (this.sourceLinks) this.sourceLinks.hidden = false;
      this.querySelector('[data-flower-title]').textContent = group.title;
      if (group.browseUrl) this.browse.href = group.browseUrl;
      else this.browse.removeAttribute('href');
      this.browse.hidden = group.selection === true || (group.browseLinks?.length || 0) > 1;
      this.browse.textContent = group.classification ? 'この分類の商品を見る →' : this.browseLabel;
      this.sourceLinks?.replaceChildren();
      if (group.browseLinks?.length > 1) for (const source of group.browseLinks) {
        if (group.title === 'その他' && ['202601:4930', '202601:4933'].includes(source.nodeId)) continue;
        const link = document.createElement('a');
        link.className = 'ace-category-flower__browse';
        link.href = source.categoryUrl;
        link.textContent = `${source.title}の商品を見る →`;
        this.sourceLinks?.append(link);
      }
      if (this.seriesButton) this.seriesButton.hidden = typeof this.cards.get(this.activeKey)?.onSeries !== 'function';
      const empty = this.querySelector('[data-flower-empty]');
      if (empty) empty.hidden = group.imageCount > 0;
    }

    preloadRelated() {
      if (this.relatedPromise) return this.relatedPromise;
      const signal = this.controller.signal;
      this.relatedPromise = (async () => {
        try {
          if (!this.dataset.relatedManifestUrl) return;
          const url = new URL(this.dataset.relatedManifestUrl, location.href);
          if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com') || url.username || url.password || url.port) return;
          const response = await fetch(url.href, { signal, credentials: 'same-origin' });
          if (!response.ok) return;
          const manifest = await response.json();
          if (signal.aborted || !this.isConnected) return;
          this.relatedCatalog = normalizeRelatedManifest(manifest, location.origin, window.AceProductDestination);
          this.refreshRelatedControls();
        } catch (_) { /* Related data must not block the first flower. */ }
      })();
      return this.relatedPromise;
    }

    refreshRelatedControls() {
      this.favoriteRecords.forEach(record => {
        if (!record.relatedButton) return;
        record.hasRelated = this.layer === 1 && relatedItems(record.item, this.relatedCatalog).length > 0;
        record.relatedButton.disabled = !record.hasRelated;
        record.li.updateFlowerReveal?.();
      });
    }

    cancelRelatedHover() {
      clearTimeout(this.relatedTimer); this.relatedTimer = null; this.relatedCandidate = null;
    }

    beginLayerTransition() {
      const hull = this.boundaryEnvelope();
      this.transitionRect = hull.length ? { left: Math.min(...hull.map(point => point.x)), right: Math.max(...hull.map(point => point.x)), top: Math.min(...hull.map(point => point.y)), bottom: Math.max(...hull.map(point => point.y)) } : null;
      this.transitionUntil = Date.now() + 650; this.cancelRelatedHover(); this.cancelBoundaryClose(); this.boundaryArmed = false;
    }

    openRelated(item, returnTarget, fromControl = false) {
      if (!this.dialog.open || this.layer !== 1 || !this.sourcePetals) return false;
      const items = relatedItems(item, this.relatedCatalog); if (!items.length) return false;
      this.relatedReturn = { sku: item.sku, fromControl, scrollTop: this.querySelector('[data-flower-viewport]').scrollTop };
      this.beginLayerTransition(); this.layer = 2; this.relatedSource = item;
      this.renderCenter(this.groups.get(this.activeKey)); this.favoriteFeedback(''); this.renderItems(items, item);
      this.querySelector('[data-flower-viewport]').scrollTop = 0;
      if (fromControl) this.backButton?.focus({ preventScroll: true });
      else this.querySelector('[data-flower-title]').focus({ preventScroll: true });
      this.queueLayout(); return true;
    }

    backRelated() {
      if (!this.dialog.open || this.layer !== 2) return false;
      const restore = this.relatedReturn, group = this.groups.get(this.activeKey);
      this.beginLayerTransition(); this.layer = 1; this.relatedSource = null; this.relatedReturn = null;
      this.relatedHoverSuppressed = true; this.relatedUnlockPoint = this.lastBoundaryPoint;
      this.renderCenter(group); this.favoriteFeedback(''); this.renderItems(group.items, group.centerItem);
      this.querySelector('[data-flower-viewport]').scrollTop = restore?.scrollTop || 0;
      const record = this.favoriteRecords.find(record => record.item.sku === restore?.sku);
      const target = restore?.fromControl ? record?.relatedButton : record?.tile;
      (target || this.querySelector('[data-flower-title]')).focus({ preventScroll: true });
      this.queueLayout(); return true;
    }

    renderItems(items, sourceItem = null) {
      this.renderController?.abort(); this.renderController = new AbortController();
      this.renderToken = (this.renderToken || 0) + 1;
      const fragment = document.createDocumentFragment();
      this.favoriteRecords = [];
      const entries = items.map(item => ({ item, source: false }));
      if (sourceItem) entries.push({ item: sourceItem, source: true });
      entries.forEach(({ item, source }, index) => {
        const li = document.createElement('li');
        li.className = 'ace-category-flower__petal';
        if (source) li.dataset.flowerSource = 'true';
        li.style.setProperty('--flower-delay', `${Math.min(index * 8, 160)}ms`);
        const tile = document.createElement('a');
        tile.className = 'ace-category-flower__product';
        tile.dataset.flowerSku = item.sku;
        const img = document.createElement('img');
        img.className = 'ace-category-flower__image';
        img.src = item.imageUrl;
        img.alt = '';
        img.width = 400;
        img.height = 360;
        img.loading = index < 8 ? 'eager' : 'lazy';
        img.decoding = 'async';
        const label = document.createElement('span');
        label.className = 'ace-category-flower__caption';
        label.textContent = source && this.layer === 1 ? `中央の候補：${item.title}` : item.title;
        label.hidden = true;
        label.setAttribute('aria-hidden', 'true');
        tile.title = item.title;
        tile.append(img);
        const status = document.createElement('span');
        status.className = 'ace-category-flower__preparing';
        tile.append(status);
        this.updateTileDestination(tile, item);
        const favorite = document.createElement('button');
        favorite.type = 'button';
        favorite.className = 'ace-category-flower__favorite';
        const star = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        star.setAttribute('viewBox', '0 0 24 24');
        star.setAttribute('aria-hidden', 'true');
        star.setAttribute('focusable', 'false');
        const starPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        starPath.setAttribute('d', 'M11.487 4.289Q12 3.25 12.513 4.289 L14.187 7.681Q14.7 8.72 15.849 8.887 L19.601 9.433Q20.75 9.6 19.92 10.411 L17.21 13.059Q16.38 13.87 16.576 15.016 L17.214 18.754Q17.41 19.9 16.382 19.358 L13.028 17.591Q12 17.05 10.972 17.591 L7.618 19.358Q6.59 19.9 6.786 18.754 L7.424 15.016Q7.62 13.87 6.79 13.059 L4.08 10.411Q3.25 9.6 4.399 9.433 L8.151 8.887Q9.3 8.72 9.813 7.681Z');
        star.append(starPath);
        favorite.append(star);
        favorite.setAttribute('data-flower-favorite-sku', item.sku);
        favorite.hidden = !this.coarseMedia.matches;
        li.append(tile, label, favorite);
        const related = document.createElement('button');
        related.type = 'button'; related.className = 'ace-category-flower__related'; related.textContent = '関連商品を見る';
        related.setAttribute('aria-label', item.title + '：関連商品を見る');
        related.setAttribute('data-flower-related-sku', item.sku); related.hidden = true;
        const record = { li, item, tile, button: favorite, relatedButton: related, hasRelated: this.layer === 1 && !source && relatedItems(item, this.relatedCatalog).length > 0 };
        related.disabled = !record.hasRelated; li.append(related); this.favoriteRecords.push(record);
        this.updateFavorite(favorite, item);
        const updateLabel = () => {
          const revealed = li.flowerHovered || li.contains(document.activeElement);
          label.hidden = (source && this.layer === 2) || !revealed;
          favorite.hidden = !(revealed || this.coarseMedia.matches);
          if (!favorite.hidden) this.positionFavorite(favorite);
          related.hidden = !record.hasRelated || !(revealed || this.coarseMedia.matches);
          li.dataset.relatedVisible = related.hidden ? 'false' : 'true';
          if (!related.hidden) this.positionRelated(related);
          li.dataset.labelVisible = label.hidden ? 'false' : 'true';
          if (!label.hidden) this.positionLabel(label);
          if (revealed) this.ensureFavorite(item);
        };
        li.updateFlowerReveal = updateLabel;
        const options = { signal: this.renderController.signal };
        li.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { li.flowerHovered = true; updateLabel(); } }, options);
        li.addEventListener('pointerleave', () => { li.flowerHovered = false; updateLabel(); }, options);
        tile.addEventListener('focus', updateLabel, options);
        favorite.addEventListener('focus', updateLabel, options);
        related.addEventListener('focus', updateLabel, options);
        for (const control of [favorite, related]) for (const type of ['pointerenter', 'pointerdown', 'focus']) control.addEventListener(type, () => this.cancelRelatedHover(), options);
        const blur = event => {
          if (li.contains(event.relatedTarget)) return;
          li.flowerHovered = false; label.hidden = true; favorite.hidden = !this.coarseMedia.matches; related.hidden = !record.hasRelated || !this.coarseMedia.matches; li.dataset.relatedVisible = related.hidden ? 'false' : 'true'; li.dataset.labelVisible = 'false';
        };
        tile.addEventListener('blur', blur, options);
        favorite.addEventListener('blur', blur, options);
        related.addEventListener('blur', blur, options);
        related.addEventListener('click', event => {
          event.preventDefault(); event.stopPropagation(); this.cancelRelatedHover();
          if (!related.disabled) this.openRelated(item, related, true);
        }, options);
        favorite.addEventListener('click', event => {
          event.preventDefault(); event.stopPropagation(); this.cancelRelatedHover(); this.cancelBoundaryClose(); this.boundaryArmed = false;
          if (this.favoriteOperations.has(item.sku)) return;
          if (favorite.getAttribute('aria-disabled') === 'true') {
            this.favoriteFeedback(this.favoriteStates.get(item.sku)?.status === 'loading' ? '商品情報を確認しています。少しお待ちください。' : 'この商品の情報は準備中です。'); return;
          }
          this.saveFavorite(item);
        }, options);
        tile.addEventListener('click', () => { this.cancelRelatedHover(); this.refreshDestinations(); }, { signal: this.renderController.signal, capture: true });
        tile.setAttribute('data-ace-destination-sku', item.sku);
        const { productId, variantId } = item.destinationItem;
        if (/^[1-9]\d*$/.test(productId || '') && /^[1-9]\d*$/.test(variantId || '')) tile.setAttribute('data-ace-destination-key', `${productId}.${variantId}`);
        if (source) this.sourcePetals.append(li); else fragment.append(li);
        updateLabel();
      });
      this.petals.replaceChildren(fragment);
      window.AceProductDestination?.bind?.(this.petals);
      if (sourceItem) window.AceProductDestination?.bind?.(this.sourcePetals);
      this.refreshDestinations();
    }

    updateFavorite(button, item) {
      const state = this.favoriteStates.get(item.sku);
      let saved = false;
      try { saved = state?.item && window.AceBoard?.list?.().some(entry => String(entry.variantId) === String(state.item.variantId)); } catch (_) {}
      const busy = this.favoriteOperations.has(item.sku);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-disabled', state?.status === 'unavailable' || state?.status === 'loading' || busy ? 'true' : 'false');
      button.setAttribute('aria-busy', state?.status === 'loading' || busy ? 'true' : 'false');
      const action = state?.status === 'unavailable' ? '商品情報準備中' : state?.status === 'loading' ? '商品情報を確認中' : saved ? 'お気に入りから外す' : 'お気に入りに保存';
      button.setAttribute('aria-label', `${item.title}：${action}`);
      button.title = action;
    }

    refreshFavorites() {
      this.favoriteRecords.forEach(({ button, item }) => this.updateFavorite(button, item));
    }

    ensureFavorite(item, retry = false) {
      const existing = this.favoriteStates.get(item.sku);
      if (existing && !(retry && existing.status === 'error')) return existing.promise || Promise.resolve(existing);
      const board = window.AceBoard;
      if (!board || typeof board.resolveSkus !== 'function') {
        const state = { status: 'unavailable', reason: 'not_ready' };
        this.favoriteStates.set(item.sku, state); this.refreshFavorites(); return Promise.resolve(state);
      }
      const state = { status: 'loading' };
      this.favoriteStates.set(item.sku, state);
      state.promise = (async () => {
        try {
          if (await board.ready !== true) { state.status = 'unavailable'; state.reason = 'not_ready'; return state; }
          const result = await board.resolveSkus([item.sku]);
          const exact = result?.items?.find(record => record.sku === item.sku && [record.productId, record.variantId].every(id => (typeof id === 'string' || Number.isSafeInteger(id)) && /^[1-9]\d*$/.test(String(id))));
          if (!exact) { state.status = 'unavailable'; state.reason = 'missing'; return state; }
          state.status = 'ready'; state.item = exact;
        } catch (_) { state.status = 'error'; }
        finally { this.refreshFavorites(); }
        return state;
      })();
      this.refreshFavorites();
      return state.promise;
    }

    favoriteFeedback(message) {
      const feedback = this.querySelector('[data-flower-feedback]');
      if (!feedback) return;
      feedback.textContent = message; feedback.hidden = !message;
      if (this.dialog.open) this.queueLayout();
    }

    async saveFavorite(item) {
      // Lock before any asynchronous lookup so one click cannot queue a second toggle.
      this.cancelRelatedHover();
      const operations = this.favoriteOperations;
      if (operations.has(item.sku)) return;
      operations.add(item.sku);
      const token = this.renderToken;
      let removing = false;
      try {
        this.refreshFavorites();
        const state = await this.ensureFavorite(item, true);
        if (!this.isConnected || !this.dialog.open || token !== this.renderToken) return;
        if (state.status !== 'ready') {
          this.favoriteFeedback(state.status === 'error' ? '商品情報を確認できませんでした。もう一度お試しください。' : 'この商品の情報は準備中です。'); return;
        }
        const board = window.AceBoard;
        const variantId = String(state.item.variantId);
        removing = board.list().some(entry => String(entry.variantId) === variantId);
        if (removing) {
          const removed = board.remove(variantId);
          const saved = board.list().some(entry => String(entry.variantId) === variantId);
          if (this.dialog.open && token === this.renderToken) this.favoriteFeedback(removed && !saved ? 'お気に入りから外しました。' : '外せませんでした。もう一度お試しください。');
          return;
        }
        const result = await board.addIds(`${state.item.productId}.${state.item.variantId}`);
        const saved = board.list().some(entry => String(entry.variantId) === variantId);
        if (this.dialog.open && token === this.renderToken) this.favoriteFeedback(result?.ok && saved ? 'お気に入りに保存しました。' : board.list().length >= 24 ? 'お気に入りは24点まで保存できます。' : '保存できませんでした。もう一度お試しください。');
      } catch (_) { if (this.dialog.open && token === this.renderToken) this.favoriteFeedback(removing ? '外せませんでした。もう一度お試しください。' : '保存できませんでした。もう一度お試しください。'); }
      finally { operations.delete(item.sku); this.refreshFavorites(); }
    }

    positionLabel(label) {
      const petal = label.parentNode;
      const offset = petal?.dataset.relatedVisible === 'true' ? 42 : 8;
      label.style.setProperty('--flower-label-x', '0px'); label.style.setProperty('--flower-label-y', String(offset) + 'px');
      const bounds = this.querySelector('[data-flower-viewport]').getBoundingClientRect(), rect = label.getBoundingClientRect();
      const width = rect.width || rect.right - rect.left, height = rect.height || rect.bottom - rect.top;
      const controls = ['.ace-category-flower__favorite', '.ace-category-flower__related'].map(selector => petal?.querySelector(selector)).filter(control => control && !control.hidden).map(control => control.getBoundingClientRect());
      const photo = petal?.children[0]?.getBoundingClientRect() || rect;
      const left = Math.max(bounds.left + 8, Math.min(rect.left, bounds.right - 8 - width));
      const above = Math.min(photo.top, ...controls.map(control => control.top)) - height - 8;
      const below = Math.max(photo.bottom, ...controls.map(control => control.bottom)) + 8;
      const overlapsControl = (x, y) => controls.some(control => x < control.right + 6 && x + width > control.left - 6 && y < control.bottom + 6 && y + height > control.top - 6);
      const candidates = [rect.top, above, below, bounds.top + 8, bounds.bottom - height - 8];
      let top = candidates.find(y => y >= bounds.top + 8 && y + height <= bounds.bottom - 8 && !overlapsControl(left, y));
      if (!Number.isFinite(top)) top = Math.max(bounds.top + 8, Math.min(rect.top, bounds.bottom - 8 - height));
      const dx = left - rect.left, dy = top - rect.top;
      label.style.setProperty('--flower-label-x', String(Number.isFinite(dx) ? dx : 0) + 'px');
      label.style.setProperty('--flower-label-y', String(offset + (Number.isFinite(dy) ? dy : 0)) + 'px');
    }

    positionFavorite(button) {
      const petal = button.parentNode;
      const smallRing = this.dialog.dataset.layout === 'rings' && !this.coarseMedia.matches && petal?.dataset.flowerSource !== 'true' && petal?.getBoundingClientRect().width <= 100;
      const initialX = smallRing ? 26 : 0, initialY = smallRing ? -26 : 0;
      button.style.setProperty('--flower-star-x', String(initialX) + 'px');
      button.style.setProperty('--flower-star-y', String(initialY) + 'px');
      const bounds = this.querySelector('[data-flower-viewport]').getBoundingClientRect(), rect = button.getBoundingClientRect();
      const dx = Math.max(bounds.left + 8 - rect.left, Math.min(0, bounds.right - 8 - rect.right));
      const dy = Math.max(bounds.top + 8 - rect.top, Math.min(0, bounds.bottom - 8 - rect.bottom));
      button.style.setProperty('--flower-star-x', String(initialX + (Number.isFinite(dx) ? dx : 0)) + 'px');
      button.style.setProperty('--flower-star-y', String(initialY + (Number.isFinite(dy) ? dy : 0)) + 'px');
    }

    positionRelated(button) {
      button.style.setProperty('--flower-related-x', '0px'); button.style.setProperty('--flower-related-y', '0px');
      const bounds = this.querySelector('[data-flower-viewport]').getBoundingClientRect(), rect = button.getBoundingClientRect();
      const dx = Math.max(bounds.left + 8 - rect.left, Math.min(0, bounds.right - 8 - rect.right));
      const dy = Math.max(bounds.top + 8 - rect.top, Math.min(0, bounds.bottom - 8 - rect.bottom));
      button.style.setProperty('--flower-related-x', String(Number.isFinite(dx) ? dx : 0) + 'px');
      button.style.setProperty('--flower-related-y', String(Number.isFinite(dy) ? dy : 0) + 'px');
    }

    updateTileDestination(tile, item) {
      const { href, kind } = item.destination;
      tile.setAttribute('aria-label', item.title + (item.inquiryOnly ? ' — ' + this.dataset.inquiry : ''));
      tile.dataset.flowerDestination = kind;
      tile.dataset.flowerInquiry = item.inquiryOnly ? 'true' : 'false';
      if (href) { tile.href = href; tile.removeAttribute('aria-disabled'); }
      else { tile.removeAttribute('href'); tile.setAttribute('aria-disabled', 'true'); }
      tile.setAttribute('tabindex', '0');
      const status = tile.querySelector('.ace-category-flower__preparing');
      status.hidden = kind === 'product' && !item.inquiryOnly;
      status.textContent = item.inquiryOnly ? this.dataset.inquiry : (kind === 'category' ? this.dataset.categoryFallback : this.dataset.preparing);
    }

    refreshDestinations() {
      const refresh = item => {
        item.destination = (item.destinationItem.classification ? classificationDestination : resolveDestination)(item.destinationItem, location.origin, window.AceProductDestination);
        item.canLink = item.destination.kind === 'product'; item.productUrl = item.canLink ? item.destination.href : null;
      };
      this.groups.forEach(group => { group.items.forEach(refresh); if (group.centerItem) refresh(group.centerItem); group.readyCount = group.items.filter(item => item.canLink).length; });
      this.favoriteRecords.forEach(({ tile, item }) => { refresh(item); this.updateTileDestination(tile, item); });
    }

    queueLayout() {
      cancelAnimationFrame(this.frame);
      if (this.dialog.open) this.frame = requestAnimationFrame(() => this.layout());
    }

    layout() {
      if (!this.dialog.open) return;
      this.cancelBoundaryClose();
      this.dialog.dataset.layout = 'rings';
      const width = this.stage.clientWidth;
      const height = this.stage.clientHeight;
      this.center.style.setProperty('--flower-center-width', `${Math.min(240, Math.max(196, width * .17))}px`);
      const center = this.center.getBoundingClientRect();
      let points = this.coarseMedia.matches ? null : ringGeometry(this.petals.children.length, width, height, center) || ringGeometry(this.petals.children.length, width, height, center, { compact: true, sidePetals: true });
      if (points && this.viewMode === 'series' && Array.from(this.petals.children).some((petal, index) => {
        const use = petal.flowerUseLine;
        return petal.children[0]?.dataset.flowerTextOnly === 'true' && use && !use.hidden && points[index].width < Math.max(use.scrollWidth || 0, [...use.textContent].length * 11) + 32;
      })) points = null;
      this.dialog.dataset.layout = points ? 'rings' : 'grid';
      this.ringPoints = points;
      Array.from(this.petals.children).forEach((petal, index) => {
        for (const property of ['x', 'y', 'width', 'height', 'rotation', 'dx', 'dy']) {
          if (points) petal.style.setProperty(`--flower-${property}`, `${points[index][property]}${property === 'rotation' ? 'deg' : 'px'}`);
          else petal.style.removeProperty(`--flower-${property}`);
        }
        const label = petal.querySelector('.ace-category-flower__caption');
        if (label && !label.hidden) this.positionLabel(label);
        petal.updateFlowerReveal?.();
      });
      for (const petal of this.sourcePetals?.children || []) petal.updateFlowerReveal?.();
    }

    boundaryEnvelope() {
      const stage = this.stage.getBoundingClientRect();
      const viewport = this.querySelector('[data-flower-viewport]').getBoundingClientRect();
      const rectangles = [];
      // Final ring geometry prevents the unfolding animation from shrinking the safe area.
      for (const point of this.ringPoints || []) {
        const angle = Math.abs(point.rotation) * Math.PI / 180;
        const width = point.width * Math.cos(angle) + point.height * Math.sin(angle);
        const height = point.height * Math.cos(angle) + point.width * Math.sin(angle);
        rectangles.push({ left: stage.left + point.x - width / 2, right: stage.left + point.x + width / 2, top: stage.top + point.y - height / 2, bottom: stage.top + point.y + height / 2 });
      }
      // Include live transformed bounds, including the enlarged hovered photograph.
      const elements = [this.center, ...Array.from(this.petals.children, (petal) => petal.children[0])];
      if (document.activeElement === this.closeButton) elements.push(this.closeButton);
      for (const petal of [...this.petals.children, ...(this.sourcePetals?.children || [])]) {
        const label = petal.querySelector('.ace-category-flower__caption');
        if (label && !label.hidden) elements.push(label);
        const favorite = petal.querySelector('.ace-category-flower__favorite');
        if (favorite && !favorite.hidden) elements.push(favorite);
        const related = petal.querySelector('.ace-category-flower__related');
        if (related && !related.hidden) elements.push(related);
      }
      for (const element of elements) {
        const rect = element.getBoundingClientRect();
        rectangles.push(this.ringPoints ? rect : { left: Math.max(rect.left, viewport.left), right: Math.min(rect.right, viewport.right), top: Math.max(rect.top, viewport.top), bottom: Math.min(rect.bottom, viewport.bottom) });
      }
      if (this.transitionRect && Date.now() < this.transitionUntil) rectangles.push(this.transitionRect);
      return outerEnvelope(rectangles);
    }

    trackBoundary(event) {
      if (!this.dialog.open || event.pointerType === 'touch' || event.buttons > 0) { this.cancelBoundaryClose(); return; }
      this.lastBoundaryPoint = { x: event.clientX, y: event.clientY };
      if (this.relatedHoverSuppressed && (!this.relatedUnlockPoint || Math.hypot(event.clientX - this.relatedUnlockPoint.x, event.clientY - this.relatedUnlockPoint.y) >= 8)) this.relatedHoverSuppressed = false;
      if (insideEnvelope(this.lastBoundaryPoint, this.boundaryEnvelope())) {
        this.boundaryArmed = true;
        this.cancelBoundaryClose();
      } else if (this.boundaryArmed) this.scheduleBoundaryClose();
    }

    cancelBoundaryClose() {
      clearTimeout(this.boundaryTimer);
      this.boundaryTimer = null;
    }

    scheduleBoundaryClose(leftViewport = false) {
      if (this.boundaryTimer || !this.dialog.open) return;
      this.boundaryTimer = setTimeout(() => {
        this.boundaryTimer = null;
        if (this.dialog.open && this.boundaryArmed && (leftViewport || !insideEnvelope(this.lastBoundaryPoint, this.boundaryEnvelope()))) this.requestClose();
      }, 120);
    }

    isEmptyBackground(target) {
      return target === this.dialog || target === this.stage || target === this.petals || target === this.querySelector('[data-flower-viewport]');
    }

    requestClose() {
      if (this.navigateBack()) return;
      this.closeUI();
      const callback = this.afterClose;
      this.afterClose = null;
      if (callback) queueMicrotask(callback);
    }

    prepareNavigation() {
      this.afterClose = null;
      this.closeUI(false, false);
    }

    lockScroll() {
      if (this.scrollLock) return;
      const properties = ['overflow', 'padding-right'];
      const body = document.body;
      this.scrollLock = { x: window.scrollX, y: window.scrollY, body: properties.map((name) => [name, body.style.getPropertyValue(name), body.style.getPropertyPriority(name)]), htmlOverflow: [document.documentElement.style.getPropertyValue('overflow'), document.documentElement.style.getPropertyPriority('overflow')] };
      const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.setProperty('overflow', 'hidden');
      body.style.setProperty('padding-right', `${padding + gap}px`);
      document.documentElement.style.setProperty('overflow', 'hidden');
    }

    unlockScroll(restorePosition = true) {
      if (!this.scrollLock) return;
      const lock = this.scrollLock;
      this.scrollLock = null;
      lock.body.forEach(([name, value, priority]) => value ? document.body.style.setProperty(name, value, priority) : document.body.style.removeProperty(name));
      if (lock.htmlOverflow[0]) document.documentElement.style.setProperty('overflow', ...lock.htmlOverflow);
      else document.documentElement.style.removeProperty('overflow');
      if (restorePosition) window.scrollTo({ left: lock.x, top: lock.y, behavior: 'instant' });
    }

    closeUI(restoreFocus = true, restorePosition = true) {
      cancelAnimationFrame(this.frame);
      this.cancelHover();
      this.cancelRelatedHover();
      this.transitionRect = null; this.layer = 1; this.relatedSource = null; this.relatedReturn = null;
      this.viewTrail = []; this.seriesView = null; this.viewMode = 'products';
      this.cancelBoundaryClose();
      this.boundaryArmed = false;
      const key = this.activeKey;
      if (key) this.hoverCooldown = Date.now() + 300;
      this.activeKey = null;
      this.internalClose = true;
      if (this.dialog?.open) this.dialog.close();
      this.internalClose = false;
      this.unlockScroll(restorePosition);
      this.cards?.get(key)?.links.forEach((link) => link.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      for (const temporary of ['series:active', 'series-product:active']) { this.groups?.delete(temporary); this.cards?.delete(temporary); }
    }

    disconnectedCallback() {
      this.ready = false;
      this.closeUI(false);
      this.controller?.abort();
      this.renderController?.abort();
      this.resizeObserver?.disconnect();
      this.cards?.forEach(({ links }) => links.forEach((link) => ['aria-haspopup', 'aria-controls', 'aria-expanded'].forEach((name) => link.removeAttribute(name))));
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports.AceCategoryFlower = AceCategoryFlower;
  window.customElements.define('ace-category-flower', AceCategoryFlower);
})();


